import { HYDRATE } from 'next-redux-wrapper';
import {
    createAction,
    createAsyncThunk,
    createSlice,
    isAnyOf,
} from '@reduxjs/toolkit';
import { addNewExpertise } from 'store/expertise';
import { deleteReq, getReq, postReq } from 'api';

const hydrate = createAction(HYDRATE);

// async thunks
export const fetchExpertise = createAsyncThunk(
    'company/fetchExpertise',
    async (id, { rejectWithValue }) => {
        try {
            const res = await getReq(`/profiles/${id}/expertise`);
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const addExpertise = createAsyncThunk(
    'company/addExpertise',
    async (expertiseDto, { dispatch, getState, rejectWithValue }) => {
        try {
            const res = await postReq(
                `/profiles/${getState().company.data.id}/expertise`,
                expertiseDto
            );
            if (!expertiseDto.id) {
                dispatch(addNewExpertise(res.data.data));
            }
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const removeExpertise = createAsyncThunk(
    'company/removeExpertise',
    async (id, { getState, rejectWithValue }) => {
        try {
            await deleteReq(
                `/profiles/${getState().company.data.id}/expertise/${id}`
            );
            return id;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

const initialState = {
    data: null,
    profileId: null,
    loading: false,
    error: null,
};
const expertiseSlice = createSlice({
    name: 'company-expertise',
    initialState,
    reducers: {
        reset: () => initialState,
    },
    extraReducers(builder) {
        builder
            .addCase(hydrate, (state, action) => {
                return {
                    ...state,
                    ...action.payload.company.expertise,
                };
            })
            .addCase(fetchExpertise.pending, (state, { meta }) => {
                state.profileId = meta.arg;
                state.data = null;
            })
            .addCase(fetchExpertise.fulfilled, (state, { payload, meta }) => {
                // Drop a late response for a previously viewed profile.
                if (meta.arg === state.profileId) {
                    state.data = payload;
                }
            })
            // A refetch pending meanwhile has cleared data and brings the saved list.
            .addCase(addExpertise.fulfilled, (state, { payload }) => {
                state.data?.push(payload);
            })
            .addCase(removeExpertise.fulfilled, (state, { payload }) => {
                if (state.data) {
                    state.data = state.data.filter(
                        (item) => item.id !== payload
                    );
                }
            })
            .addMatcher(
                isAnyOf(
                    fetchExpertise.fulfilled,
                    addExpertise.fulfilled,
                    removeExpertise.fulfilled
                ),
                (state) => {
                    state.loading = false;
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    fetchExpertise.pending,
                    addExpertise.pending,
                    removeExpertise.pending
                ),
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    fetchExpertise.rejected,
                    addExpertise.rejected,
                    removeExpertise.rejected
                ),
                (state, { payload }) => {
                    state.loading = false;
                    state.error = payload;
                }
            );
    },
});

export const { reset } = expertiseSlice.actions;

export const companyExpertiseSelector = ({ company }) => company.expertise;

export default expertiseSlice.reducer;
